import { Controller, useFormContext } from "react-hook-form";
import { cn } from "@/utils/cn";
import { cva } from "class-variance-authority";
import { Field, Label, Switch as HeadlessSwitch } from "@headlessui/react";

const fieldVariants = cva("flex items-center gap-2", {
    variants: {
        labelPlacement: {
            start: "flex-row",
            end: "flex-row-reverse",
            top: "flex-col",
            bottom: "flex-col-reverse",
        },
        size: {
            default: "text-base",
            sm: "text-sm",
            lg: "text-lg",
        },
    },
    defaultVariants: {
        labelPlacement: "start",
        size: "default",
    },
});

const switchVariants = cva(
    "group inline-flex items-center rounded-full bg-white outline-1 transition focus-visible:shadow-[0_0_0_0.25rem] focus-visible:outline-2 focus-visible:-outline-offset-2 data-checked:outline-0 data-disabled:opacity-50",
    {
        variants: {
            variant: {
                primary:
                    "shadow-darkgreen-500/25 outline-gray-400 data-checked:bg-darkgreen-500",
                secondary:
                    "shadow-emerald-950/25 outline-gray-400 data-checked:bg-emerald-950",
                success:
                    "shadow-lime-500/25 outline-lime-500 data-checked:bg-lime-500",
                danger: "shadow-red-500/25 outline-red-500 data-checked:bg-red-500",
            },
            size: {
                default: "h-6 w-11",
                sm: "h-5.25 w-9.5",
                lg: "h-6.75 w-12.5",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
        },
    },
);

const switchIconVariants = cva("translate-x-1 rounded-full transition", {
    variants: {
        variant: {
            primary: "bg-gray-400 group-data-checked:bg-white",
            secondary: "bg-gray-400 group-data-checked:bg-white",
            success: "bg-lime-500 group-data-checked:bg-white",
            danger: "bg-red-500 group-data-checked:bg-white",
        },
        size: {
            default: "size-4 group-data-checked:translate-x-6",
            sm: "size-3.5 group-data-checked:translate-x-5",
            lg: "size-4.5 group-data-checked:translate-x-7",
        },
    },
    defaultVariants: {
        variant: "primary",
        size: "default",
    },
});

export const Switch = ({
    label,
    name,
    className,
    disabled,
    hideLabel = false,
    variant,
    labelPlacement,
    size,
    onChange,
}) => {
    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={0}
            render={({ field }) => (
                <Field
                    disabled={disabled}
                    className={cn(
                        fieldVariants({ labelPlacement, size, className }),
                    )}
                >
                    <Label
                        className={cn(
                            "inline-block text-darkgreen-500 data-disabled:opacity-50",
                            hideLabel ? "sr-only" : "",
                        )}
                    >
                        {label}
                    </Label>
                    <HeadlessSwitch
                        checked={field.value}
                        onChange={(checked) => {
                            field.onChange(checked);
                            onChange?.(checked);
                        }}
                        className={cn(switchVariants({ variant, size }))}
                    >
                        <span
                            className={cn(
                                switchIconVariants({ variant, size }),
                            )}
                        />
                    </HeadlessSwitch>
                </Field>
            )}
        />
    );
};
